import axios, { AxiosInstance, AxiosError } from "axios";

type Props = {
  pathUrl: string;
  payload?: object;
  skipAuthLogout?: boolean;
};

export const API_BASE_URL = import.meta.env.VITE_BASE_URL;

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Helper function to handle axios errors
const handleAxiosError = (error: unknown, skipAuthLogout = false) => {
  if (error instanceof AxiosError) {
    if (error.response?.status === 401 && !skipAuthLogout) {
      // Clear token and redirect to login if unauthorized
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
    // Always throw the error so the mutation's onError can handle it
    throw error.response?.data || { message: error.message };
  }
  throw { message: "An unexpected error occurred" };
};

// Add token to all requests
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Content-Type"] = "application/json";
        config.headers["Accept"] = "*/*";
      }
      return config;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Resource methods with error handling
export const getResource = async ({ pathUrl }: Props) => {
  try {
    const { data } = await axiosInstance.get(pathUrl);
    return data;
  } catch (error) {
    handleAxiosError(error);
  }
};

export const postResource = async ({ pathUrl, payload }: Props) => {
  try {
    const { data } = await axiosInstance.post(pathUrl, payload);
    return data;
  } catch (error) {
    handleAxiosError(error);
  }
};

export const updateResource = async ({ pathUrl, payload }: Props) => {
  try {
    const { data } = await axiosInstance.put(pathUrl, payload);
    return data;
  } catch (error) {
    handleAxiosError(error);
  }
};

export const patchResource = async ({ pathUrl, payload, skipAuthLogout }: Props) => {
  try {
    const { data } = await axiosInstance.patch(pathUrl, payload);
    return data;
  } catch (error) {
    handleAxiosError(error, skipAuthLogout);
  }
};

export const deleteResource = async ({ pathUrl, payload }: Props) => {
  try {
    const { data } = await axiosInstance.delete(pathUrl, { data: payload });
    return data;
  } catch (error) {
    handleAxiosError(error);
  }
};